/**
 * Reports — Step 34.
 *
 * The CURATED report, in the reference's own sense: fixed widgets, and the
 * only thing a reader changes is the filters. Its Analytics glossary draws
 * that line explicitly, and the custom-report builder is Step 50 — shipping
 * half of one would be worse than shipping neither.
 *
 * ── Two rules this page is built on ────────────────────────────────────
 *
 * 1. **A number we cannot measure is ABSENT, never zero.** Time spent, survey
 *    scores, task and asset counts all appear in the reference's glossary and
 *    none of them exist in our data. A zero on a dashboard reads as a
 *    measurement rather than an absence, which is the easiest way to make a
 *    report lie.
 * 2. **Business hours and calendar hours are labelled separately.** Business
 *    durations come from SLA clocks and therefore only cover tickets a policy
 *    matched; calendar durations cover every ticket. Blending them would
 *    overstate performance on exactly the tickets nobody wrote a policy for.
 *
 * Every filter lives in the URL (the Global Standard), so a link to "last 90
 * days, Broadcast Engineering" is a link someone else opens and sees the same
 * numbers.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, BarChart3, Minus } from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent } from '../components/ui/card';
import { Select } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { apiFetch } from '../lib/api';
import { qk } from '../lib/queryClient';
import { useUrlState } from '../lib/urlState';
import { ticketsApi } from '../features/tickets/api';
import { cn } from '../lib/utils';

interface Report {
  range: { from: number; to: number; timeZone: string };
  totals: {
    created: number;
    createdDelta: number | null;
    resolved: number;
    resolvedDelta: number | null;
    backlog: number;
    reopened: number;
    firstResponseMedianMs: number | null;
    firstResponseP90Ms: number | null;
    resolutionMedianMs: number | null;
    resolutionP90Ms: number | null;
    firstResponseBusinessMedianMs: number | null;
    resolutionBusinessMedianMs: number | null;
    slaFirstResponseRate: number | null;
    slaFirstResponseCount: number;
    slaResolutionRate: number | null;
    slaResolutionCount: number;
    fcrRate: number | null;
    agentReplies: number;
    requesterReplies: number;
    privateNotes: number;
    avgAgentRepliesPerTicket: number | null;
    avgRequesterRepliesPerTicket: number | null;
  };
  breakdowns: Record<string, { key: string; count: number }[]>;
  trend: { created: { day: string; count: number }[]; resolved: { day: string; count: number }[] };
  truncated: boolean;
}

const RANGES = [
  { key: '7', label: 'Last 7 days' },
  { key: '30', label: 'Last 30 days' },
  { key: '90', label: 'Last 90 days' },
  { key: '365', label: 'Last 12 months' },
];

/** Durations read as a person would say them — "4h 20m", never "15600000". */
function duration(ms: number | null): string {
  if (ms === null) return '—';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);

/**
 * A scorecard. `delta` is the reference's green/red arrow, and its direction
 * of GOODNESS differs per metric — more tickets resolved is good, more
 * backlog is not. Getting that backwards paints a failing desk green.
 */
function Metric({
  label,
  value,
  sub,
  delta,
  higherIsBetter,
}: {
  label: string;
  value: string;
  sub?: string;
  delta?: number | null;
  higherIsBetter?: boolean;
}) {
  const good = delta == null ? null : higherIsBetter === undefined ? null : delta > 0 === higherIsBetter;
  const Icon = delta == null ? Minus : delta > 0 ? ArrowUp : ArrowDown;
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
        <div className="mt-1 flex items-center gap-2 text-xs">
          {delta != null && (
            <span
              className={cn(
                'flex items-center gap-0.5 tabular-nums',
                good === null
                  ? 'text-muted-foreground'
                  : good
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-destructive',
              )}
            >
              <Icon className="h-3 w-3" />
              {Math.abs(Math.round(delta * 100))}%
            </span>
          )}
          {sub && <span className="text-muted-foreground">{sub}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function Breakdown({ title, rows }: { title: string; rows: { key: string; count: number }[] }) {
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-4">
        <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">{title}</p>
        <div style={{ height: Math.max(120, rows.length * 28) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 16 }}>
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="key"
                width={130}
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip cursor={{ fill: 'hsl(var(--muted))' }} />
              <Bar dataKey="count" fill="hsl(var(--brand))" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

export function ReportsPage() {
  const [days, setDays] = useUrlState('range', '30');
  const [groupId, setGroupId] = useUrlState('group', null);

  const { from, to } = useMemo(() => {
    const now = Date.now();
    return { from: now - Number(days || 30) * 86_400_000, to: now };
    // The window is recomputed only when the RANGE changes, not on every
    // render — otherwise `to` moves by milliseconds and the query key with it,
    // refetching forever.
  }, [days]);

  const groups = useQuery({ queryKey: qk.orgGroups, queryFn: ticketsApi.groups, staleTime: 5 * 60_000 });
  const report = useQuery({
    queryKey: qk.serviceDeskReport(from, to, groupId),
    queryFn: () =>
      apiFetch<Report>(
        `/api/reports/service-desk?from=${from}&to=${to}${groupId ? `&groupId=${groupId}` : ''}`,
      ),
  });

  const trend = useMemo(() => {
    if (!report.data) return [];
    const resolved = new Map(report.data.trend.resolved.map((d) => [d.day, d.count]));
    return report.data.trend.created.map((d) => ({
      day: d.day.slice(5),
      Created: d.count,
      Resolved: resolved.get(d.day) ?? 0,
    }));
  }, [report.data]);

  return (
    <div className="mx-auto w-full max-w-6xl p-6">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <BarChart3 className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Service desk at a glance</h1>
        <div className="flex-1" />
        <Select
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="h-8 w-40"
          aria-label="Range"
        >
          {RANGES.map((r) => (
            <option key={r.key} value={r.key}>
              {r.label}
            </option>
          ))}
        </Select>
        <Select
          value={groupId ?? ''}
          onChange={(e) => setGroupId(e.target.value || null)}
          className="h-8 w-48"
          aria-label="Group"
        >
          <option value="">All groups</option>
          {(groups.data?.entries ?? []).map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </Select>
      </div>

      {/* The mandatory order: error → loading → empty → content. */}
      {report.isError ? (
        <Card>
          <CardContent className="flex flex-col items-start gap-3 p-6">
            <p className="text-sm">That report could not be loaded.</p>
            <Button variant="outline" size="sm" onClick={() => void report.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : report.isPending ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : report.data.totals.created === 0 && report.data.totals.backlog === 0 ? (
        <Card>
          <CardContent className="p-10 text-center">
            <p className="text-sm text-muted-foreground">No tickets in this period.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {report.data.truncated && (
            <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
              This period has more tickets than one report can read, so the figures cover the most recent
              only. Narrow the range for exact numbers.
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric
              label="Created"
              value={String(report.data.totals.created)}
              delta={report.data.totals.createdDelta}
              sub="vs previous period"
            />
            <Metric
              label="Resolved"
              value={String(report.data.totals.resolved)}
              delta={report.data.totals.resolvedDelta}
              higherIsBetter
              sub="vs previous period"
            />
            <Metric label="Open now" value={String(report.data.totals.backlog)} sub="backlog" />
            <Metric
              label="Reopened"
              value={String(report.data.totals.reopened)}
              sub="a second SLA cycle ran"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric
              label="First response"
              value={duration(report.data.totals.firstResponseMedianMs)}
              sub={`median · p90 ${duration(report.data.totals.firstResponseP90Ms)}`}
            />
            <Metric
              label="Resolution"
              value={duration(report.data.totals.resolutionMedianMs)}
              sub={`median · p90 ${duration(report.data.totals.resolutionP90Ms)}`}
            />
            <Metric
              label="SLA — first response"
              value={pct(report.data.totals.slaFirstResponseRate)}
              sub={`${report.data.totals.slaFirstResponseCount} under a policy`}
            />
            <Metric
              label="SLA — resolution"
              value={pct(report.data.totals.slaResolutionRate)}
              sub={`${report.data.totals.slaResolutionCount} under a policy`}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric
              label="First-contact resolution"
              value={pct(report.data.totals.fcrRate)}
              sub="resolved in one reply, never reopened"
            />
            <Metric
              label="Agent replies"
              value={
                report.data.totals.avgAgentRepliesPerTicket === null
                  ? '—'
                  : report.data.totals.avgAgentRepliesPerTicket.toFixed(1)
              }
              sub="average per ticket"
            />
            <Metric
              label="Requester replies"
              value={
                report.data.totals.avgRequesterRepliesPerTicket === null
                  ? '—'
                  : report.data.totals.avgRequesterRepliesPerTicket.toFixed(1)
              }
              sub="average per ticket"
            />
            {/* Business hours are a DIFFERENT population, so they get their own
                card and say so rather than sitting beside the calendar figure
                as if they were comparable. */}
            <Metric
              label="In business hours"
              value={duration(report.data.totals.firstResponseBusinessMedianMs)}
              sub={`first response · resolution ${duration(report.data.totals.resolutionBusinessMedianMs)}`}
            />
          </div>

          <Card>
            <CardContent className="p-4">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Volume — created vs resolved
              </p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend} margin={{ left: -20, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="Created"
                      stroke="hsl(var(--brand))"
                      fill="hsl(var(--brand))"
                      fillOpacity={0.15}
                    />
                    <Area
                      type="monotone"
                      dataKey="Resolved"
                      stroke="hsl(142 71% 45%)"
                      fill="hsl(142 71% 45%)"
                      fillOpacity={0.12}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Days are counted in {report.data.range.timeZone}, the same timezone your SLA clocks run on.
              </p>
            </CardContent>
          </Card>

          <div className="grid gap-3 lg:grid-cols-2">
            <Breakdown title="By source" rows={report.data.breakdowns.source ?? []} />
            <Breakdown title="By priority" rows={report.data.breakdowns.priority ?? []} />
            <Breakdown title="By group" rows={report.data.breakdowns.group ?? []} />
            <Breakdown title="By type" rows={report.data.breakdowns.type ?? []} />
          </div>

          {/* Stated, not silently omitted — the settings index's rule. */}
          <p className="pt-2 text-xs text-muted-foreground">
            Time spent, satisfaction scores, task and asset counts are not shown because we do not record them
            yet. They arrive with the steps that do.
          </p>
        </div>
      )}
    </div>
  );
}
